import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { ChatMessage } from '../context/chatTypes';
import { downloadAndDecryptAttachment } from '../services/attachmentService';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faVideo, faMusic, faFile, faSpinner, faCircleExclamation, faDownload } from '@fortawesome/free-solid-svg-icons';

interface AttachmentRendererProps {
  message: ChatMessage;
}

// Sub-component for voice notes: auto-decrypt and render inline player
const VoiceNoteAttachment: React.FC<{
  content: any;
  mimeType: string;
  isSentByMe: boolean;
}> = ({ content, mimeType, isSentByMe }) => {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current || !content) return;
    hasStarted.current = true;

    downloadAndDecryptAttachment(content)
      .then((result) => {
        const blob = new Blob([result.data], { type: result.mimeType || mimeType });
        setAudioBlob(blob);
      })
      .catch((err) => {
        console.error('Failed to decrypt voice note:', err);
        setError('Failed to load voice message');
      })
      .finally(() => setIsLoading(false));
  }, [content, mimeType]);

  if (isLoading) {
    return (
      <div className="voice-player voice-player--loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <span>Loading voice message...</span>
      </div>
    );
  }

  if (error || !audioBlob) {
    return (
      <div className="voice-player voice-player--error">
        <FontAwesomeIcon icon={faCircleExclamation} />
        <span>{error || 'Error'}</span>
      </div>
    );
  }

  return (
    <VoiceMessagePlayer
      audioBlob={audioBlob}
      durationSecs={content?.durationSecs}
      mimeType={mimeType}
      isSentByMe={isSentByMe}
    />
  );
};

const AttachmentRenderer: React.FC<AttachmentRendererProps> = ({ message }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [decryptedFilename, setDecryptedFilename] = useState<string>('file');
  const [error, setError] = useState<string | null>(null);
  const hasStartedAutoDecrypt = useRef(false);

  const content = message.content;
  const fileName = content?.filename || content?.fileName || 'file';
  const mimeType = content?.mimeType || 'application/octet-stream';
  const fileSize = content?.fileSize || content?.contentLength;
  const isImage = mimeType.startsWith('image/');
  const isVoiceNote = content?.isVoiceNote === true && mimeType.startsWith('audio/');

  // Auto-decrypt images on mount
  useEffect(() => {
    if (isImage && content && !hasStartedAutoDecrypt.current) {
      hasStartedAutoDecrypt.current = true;
      setIsDownloading(true);
      downloadAndDecryptAttachment(content)
        .then((result) => {
          const blob = new Blob([result.data], { type: result.mimeType });
          const url = URL.createObjectURL(blob);
          setDecryptedUrl(url);
          setDecryptedFilename(result.filename);
        })
        .catch((err) => {
          console.error('Failed to auto-decrypt image:', err);
          setError('Failed to load image');
        })
        .finally(() => setIsDownloading(false));
    }
  }, [isImage, content]);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (decryptedUrl) URL.revokeObjectURL(decryptedUrl);
    };
  }, [decryptedUrl]);

  const triggerDownload = useCallback((url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const handleDownload = useCallback(async () => {
    if (isDownloading || !content) return;

    // If already decrypted, just trigger the save
    if (decryptedUrl) {
      triggerDownload(decryptedUrl, decryptedFilename);
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      const result = await downloadAndDecryptAttachment(content);
      const blob = new Blob([result.data], { type: result.mimeType });
      const url = URL.createObjectURL(blob);
      setDecryptedUrl(url);
      setDecryptedFilename(result.filename);
      triggerDownload(url, result.filename);
    } catch (err: any) {
      console.error('Failed to download attachment:', err);
      setError('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  }, [content, isDownloading, decryptedUrl, decryptedFilename, triggerDownload]);

  // Re-scroll to bottom when an image loads and expands, if user was near the bottom.
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const scrollContainer = img.closest('.chat-message-thread');
    if (!scrollContainer) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const threshold = img.clientHeight + 100;
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      const anchor = scrollContainer.querySelector('[data-scroll-anchor]');
      if (anchor) {
        anchor.scrollIntoView({ block: 'end' });
      } else {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  if (!content) return <div className="chat-attachment__error">Invalid attachment</div>;

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Voice note: inline player
  if (isVoiceNote) {
    return (
      <VoiceNoteAttachment
        content={content}
        mimeType={mimeType}
        isSentByMe={message.isSentByMe}
      />
    );
  }

  // Image: show loading, then inline preview with download overlay
  if (isImage) {
    if (isDownloading) {
      return (
        <div className="chat-attachment chat-attachment--image-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>{fileName}</span>
        </div>
      );
    }

    if (decryptedUrl) {
      return (
        <div className="chat-attachment chat-attachment--image">
          <div className="chat-attachment__image-wrap">
            <img
              src={decryptedUrl}
              alt={fileName}
              className="chat-attachment__image"
              onLoad={handleImageLoad}
            />
            <button
              className="chat-attachment__download-btn"
              onClick={() => triggerDownload(decryptedUrl, decryptedFilename)}
              title="Download"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="chat-attachment chat-attachment--image-loading" style={{ color: '#ff4444' }}>
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{error}</span>
        </div>
      );
    }

    return null;
  }

  // Non-image: show download card
  return (
    <div className="chat-attachment chat-attachment--file" onClick={handleDownload}>
      <div className="chat-attachment__icon">
        {mimeType.includes('pdf') ? (
          <FontAwesomeIcon icon={faFilePdf} />
        ) : mimeType.includes('video') ? (
          <FontAwesomeIcon icon={faVideo} />
        ) : mimeType.includes('audio') ? (
          <FontAwesomeIcon icon={faMusic} />
        ) : (
          <FontAwesomeIcon icon={faFile} />
        )}
      </div>
      <div className="chat-attachment__info">
        <div className="chat-attachment__filename">{fileName}</div>
        {fileSize && (
          <div className="chat-attachment__size">{formatFileSize(fileSize)}</div>
        )}
      </div>
      <div className="chat-attachment__action">
        {isDownloading ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : error ? (
          <FontAwesomeIcon icon={faCircleExclamation} style={{ color: '#ff4444' }} />
        ) : (
          <FontAwesomeIcon icon={faDownload} />
        )}
      </div>
      {error && <div className="chat-attachment__error">{error}</div>}
    </div>
  );
};

export default AttachmentRenderer;
